// @ts-check
/* eslint-env browser */
import { reactiveStore } from '../reactive-store.js'

/** @typedef {import('../base-map/index.js').BaseMap} BaseMap */

export class KeyboardEditor extends HTMLElement {
  #MOVEMENT_AMOUNT = 0.5

  /** @type {HTMLElement | null} */
  #mapContainer = null

  /** @type {boolean} */
  #isFocused = false

  /** @type {boolean} */
  #focusedByKeyboard = false

  /** @type {HTMLCanvasElement | null} */
  #canvas = null

  connectedCallback () {
    this.#mapContainer = this.closest('map-container')
    this.#render()
    document.addEventListener('keydown', this.#boundHandleKeyDown)
    document.addEventListener('focusin', this.#boundHandleFocusIn)

    reactiveStore.mapReady.subscribe(() => {
      this.#setupCanvasListeners()
    })
  }

  disconnectedCallback () {
    document.removeEventListener('keydown', this.#boundHandleKeyDown)
    document.removeEventListener('focusin', this.#boundHandleFocusIn)
    this.#removeCanvasListeners()
  }

  #setupCanvasListeners () {
    this.#canvas = /** @type {HTMLCanvasElement | null} */ (this.#getBaseMap()?.querySelector('canvas'))
    if (!this.#canvas) {
      return
    }

    this.#canvas.addEventListener('focus', this.#boundHandleFocus)
    this.#canvas.addEventListener('blur', this.#boundHandleBlur)
  }

  #removeCanvasListeners () {
    if (this.#canvas) {
      this.#canvas.removeEventListener('focus', this.#boundHandleFocus)
      this.#canvas.removeEventListener('blur', this.#boundHandleBlur)
    }
  }

  #render () {
    this.innerHTML = `
      <div class="map__keyboard-mode-alert govuk-body" aria-live="assertive">Keyboard edit mode enabled</div>
      <div class="keyboard-pointer" style="top: 50%; left: 50%;">
        <div class="keyboard-pointer__inner">
          <div class="keyboard-pointer__horizontal"></div>
          <div class="keyboard-pointer__vertical"></div>
        </div>
      </div>
    `
  }

  /**
   * Gets the coordinates of the keyboard pointer
   * @returns {Object} The coordinates of the keyboard pointer
   */
  #getPointerCoords = () => {
    const rect = this.querySelector('.keyboard-pointer')?.getBoundingClientRect()
    return {
      x: rect?.left,
      y: rect?.top
    }
  }

  /**
   * Dispatches a mousemove event to interact with the map
   */
  #dispatchMouseMove = () => {
    const newCoords = this.#getPointerCoords()
    if (!newCoords?.x || !newCoords?.y) {
      return
    }
    const mouseMoveEvent = new MouseEvent('mousemove', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: newCoords.x,
      clientY: newCoords.y
    })
    document.elementFromPoint(
      newCoords.x,
      newCoords.y
    )?.dispatchEvent(mouseMoveEvent)
  }

  // To ensure `this` is passed to #handleKeyDown
  #boundHandleKeyDown = (event) => {
    if (event.key === 'Tab') {
      this.#focusedByKeyboard = true
    }
    this.#handleKeyDown(event)
  }

  #boundHandleFocusIn = (event) => {
    if (event.target !== this.#canvas) {
      this.#focusedByKeyboard = false
    }
  }

  #boundHandleFocus = () => {
    if (!this.#focusedByKeyboard) {
      return
    }
    this.#focusedByKeyboard = false
    this.#isFocused = true
    this.classList.add('keyboard-editor---edit-mode')
    this.#getMap()?.keyboard.disable()
    this.#showAlert('Keyboard edit mode enabled')
  }

  #boundHandleBlur = () => {
    const wasFocused = this.#isFocused
    this.#isFocused = false
    this.#focusedByKeyboard = false
    this.classList.remove('keyboard-editor---edit-mode')
    this.#getMap()?.keyboard.enable()
    if (wasFocused) {
      this.#showAlert('Keyboard edit mode disabled')
    }
  }

  /**
   * @returns {BaseMap | null}
   */
  #getBaseMap () {
    return /** @type {BaseMap | null} */ (this.#mapContainer?.querySelector('base-map'))
  }

  #getMap () {
    return this.#getBaseMap()?.map
  }

  #handleKeyDown (event) {
    if (!this.#isFocused) {
      return
    }

    /** @type {HTMLElement | null} */
    const keyboardPointer = this.querySelector('.keyboard-pointer')
    if (!keyboardPointer) {
      return
    }

    const movementAmount = this.#MOVEMENT_AMOUNT * (event.shiftKey ? 10 : 1)

    // arrow keys
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      const currentTop = parseFloat(keyboardPointer.style.top)
      const newPos = Math.max(0, currentTop - movementAmount)
      if (newPos === 0) {
        this.#getMap()?.panBy([0, -100])
      }
      keyboardPointer.style.top = `${newPos}%`
      this.#dispatchMouseMove()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      const currentTop = parseFloat(keyboardPointer.style.top)
      const newPos = Math.min(100, currentTop + movementAmount)
      if (newPos === 100) {
        this.#getMap()?.panBy([0, 100])
      }
      keyboardPointer.style.top = `${newPos}%`
      this.#dispatchMouseMove()
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      const currentLeft = parseFloat(keyboardPointer.style.left)
      const newPos = Math.max(0, currentLeft - movementAmount)
      if (newPos === 0) {
        this.#getMap()?.panBy([-100, 0])
      }
      keyboardPointer.style.left = `${newPos}%`
      this.#dispatchMouseMove()
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      const currentLeft = parseFloat(keyboardPointer.style.left)
      const newPos = Math.min(100, currentLeft + movementAmount)
      if (newPos === 100) {
        this.#getMap()?.panBy([100, 0])
      }
      keyboardPointer.style.left = `${newPos}%`
      this.#dispatchMouseMove()

    // zoom keys
    } else if (event.key === '-') {
      event.preventDefault()
      this.#getMap()?.zoomOut()
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault()
      this.#getMap()?.zoomIn()

    // enter key
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const coords = this.#getPointerCoords()
      if (!coords?.x || !coords?.y) {
        return
      }
      const target = document.elementFromPoint(coords.x, coords.y)

      // Trigger a click event for selecting/deselecting the feature
      target?.dispatchEvent(new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
        clientX: coords.x,
        clientY: coords.y
      }))

      // check what type of event we want to dispatch (based on the edit mode)
      const editMode = reactiveStore.editMode.get()
      if (editMode === 'cut' || editMode === 'polygon' || editMode === 'magic_wand') {
        keyboardPointer.dataset.mode = 'click'
      } else if (keyboardPointer.dataset.mode === 'mousedown') {
        keyboardPointer.dataset.mode = 'mouseup'
      } else {
        keyboardPointer.dataset.mode = 'mousedown'
      }

      const mouseClickEvent = new MouseEvent(keyboardPointer.dataset.mode, {
        view: window,
        bubbles: true,
        cancelable: true,
        clientX: coords.x,
        clientY: coords.y
      })
      target?.dispatchEvent(mouseClickEvent)
    }
  }

  #showAlert (message) {
    /** @type {HTMLElement | null} */
    const alert = this.querySelector('.map__keyboard-mode-alert')
    if (!alert) {
      return
    }
    alert.textContent = message
    alert.style.visibility = 'visible'
    window.setTimeout(() => {
      alert.style.visibility = 'hidden'
    }, 3000)
  }
}

customElements.define('keyboard-editor', KeyboardEditor)
